import api from "./apiService";

/**
 * Fetch all available communities
 * @returns {Promise<Object>} Communities data
 */
export const fetchCommunities = async () => {
    try {
        const response = await api.get("/communities");
        return response.data || { communities: [] };
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch the communities the current user belongs to
 * @returns {Promise<Array>} User's communities with roles
 */
export const fetchUserCommunities = async () => {
    try {
        const response = await api.get("/user/communities");
        const communities = response.data || [];
        
        // For each community, fetch the full details to get allow_member_edits
        const communitiesWithDetails = await Promise.all(
            communities.map(async (community) => {
                try {
                    // Fetch full community details
                    const details = await getCommunity(community.id);
                    
                    // Merge the original community data with the detailed info
                    return {
                        ...community,
                        allow_member_edits: details.allow_member_edits,
                        visibility: details.visibility,
                        supported_event_types: details.supported_event_types,
                        parsed_event_types: details.parsed_event_types
                    };
                } catch (error) {
                    return community; // Return original if fetch fails
                }
            })
        );
        
        return communitiesWithDetails;
    } catch (error) {
        throw error;
    }
};

/**
 * Set a community as the user's primary community
 * @param {number} communityId The ID of the community to set as primary
 * @returns {Promise<Object>} Response data
 */
export const setPrimaryCommunity = async (communityId) => {
    try {
        const response = await api.put("/user/communities/primary", { community_id: communityId });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Request to join a community
 * @param {number} communityId The ID of the community to join
 * @param {boolean} makePrimary Whether to make this the primary community
 * @returns {Promise<Object>} Response data
 */
export const joinCommunity = async (communityId, makePrimary = false) => {
    try {
        const response = await api.post(`/communities/${communityId}/join`, { make_primary: makePrimary });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Leave a community
 * @param {number} communityId The ID of the community to leave
 * @returns {Promise<Object>} Response data
 */
export const leaveCommunity = async (communityId) => {
    try {
        const response = await api.delete(`/user/communities/${communityId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get the users in a specific community
 * @param {number} communityId The ID of the community
 * @returns {Promise<Array>} List of users in the community
 */
export const getCommunityUsers = async (communityId) => {
    try {
        const response = await api.get(`/communities/${communityId}/users`);
        return response.data || [];
    } catch (error) {
        throw error;
    }
};

/**
 * Search for users to invite to a community
 * @param {number} communityId The ID of the community
 * @param {string} searchTerm The search term (display name or exact email)
 * @returns {Promise<Array>} List of users matching the search (id, display_name, profile_image, is_member)
 */
export const searchUsersForInvite = async (communityId, searchTerm) => {
    try {
        const response = await api.get(`/communities/${communityId}/search-users`, {
            params: { q: searchTerm }
        });
        return response.data || [];
    } catch (error) {
        throw error;
    }
};

/**
 * Send an invitation to join a community
 * @param {number} communityId The ID of the community
 * @param {Object} options Invitation options
 * @param {string} [options.email] The email to send the invitation to (for new users or direct email invite)
 * @param {number} [options.userId] The user ID to invite (for existing users found via search)
 * @param {boolean} [options.makePrimary] Whether to make this the primary community for the user
 * @returns {Promise<Object>} Response data with information if user is new or existing
 */
export const sendCommunityInvite = async (communityId, emailOrOptions, makePrimary = false) => {
    try {
        // Support both old signature (email, makePrimary) and new signature (options object)
        let payload;
        if (typeof emailOrOptions === 'string') {
            // Old signature: sendCommunityInvite(communityId, email, makePrimary)
            payload = {
                email: emailOrOptions,
                make_primary: makePrimary
            };
        } else {
            // New signature: sendCommunityInvite(communityId, { email, userId, makePrimary })
            payload = {
                email: emailOrOptions.email,
                user_id: emailOrOptions.userId,
                make_primary: emailOrOptions.makePrimary || false
            };
        }
        const response = await api.post(`/communities/${communityId}/invites`, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Accept a community invitation
 * @param {string} token The invitation token
 * @param {number} communityId The ID of the community
 * @param {boolean} makePrimary Whether to make this the primary community
 * @returns {Promise<Object>} Response data
 */
export const acceptInvitation = async (token, communityId, makePrimary = false) => {
    try {
        const response = await api.post(`/communities/accept-invitation`, {
            token,
            community_id: communityId,
            make_primary: makePrimary
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new community
 * @param {Object} communityData The community data including name, description, visibility, supported_event_types, and allow_member_edits
 * @returns {Promise<Object>} Response data
 */
export const createCommunity = async (communityData) => {
    try {
        const response = await api.post("/communities", communityData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update community settings
 * @param {number} communityId The ID of the community to update
 * @param {Object} communityData The updated community data including name, description, visibility, supported_event_types, and allow_member_edits
 * @returns {Promise<Object>} Response data
 */
export const updateCommunity = async (communityId, communityData) => {
    try {
        const response = await api.put(`/communities/${communityId}`, communityData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Join a newly created community as an admin
 * @param {number} communityId The ID of the community
 * @param {boolean} makePrimary Whether to make this the primary community
 * @returns {Promise<Object>} Response data
 */
export const joinCommunityAsAdmin = async (communityId, makePrimary = true) => {
    try {
        const response = await api.post(`/communities/${communityId}/admin/join`, { 
            make_primary: makePrimary 
        });
        return response.data;
    } catch (error) {
        // If the error is because the user is already a member, pass this message up
        // so the UI can handle it appropriately
        if (error.response?.data?.message?.includes('already a member')) {
            return { 
                message: 'You are already a member of this community',
                alreadyMember: true
            };
        }
        throw error;
    }
};

/**
 * Get a specific community by ID
 * @param {number} communityId The ID of the community to fetch
 * @returns {Promise<Object>} Community data
 */
export const getCommunity = async (communityId) => {
    try {
        const response = await api.get(`/communities/${communityId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update a user's role in a community (admin only)
 * @param {number} communityId The ID of the community
 * @param {number} userId The ID of the user
 * @param {string} role The new role ('user' or 'community_admin')
 * @returns {Promise<Object>} Response data
 */
export const updateUserRole = async (communityId, userId, role) => {
    try {
        const response = await api.put(`/communities/${communityId}/admin/users/${userId}/role`, { role });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove a user from a community (admin only)
 * @param {number} communityId The ID of the community
 * @param {number} userId The ID of the user to remove
 * @returns {Promise<Object>} Response data
 */
export const removeCommunityUser = async (communityId, userId) => {
    try {
        const response = await api.delete(`/communities/${communityId}/admin/users/${userId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Process an invitation to join a community
 * @param {number} communityId The ID of the community
 * @param {string} token The invitation token
 * @param {boolean} makePrimary Whether to make this the primary community
 * @returns {Promise<Object>} Response data
 */
export const processInvitation = async (communityId, token, makePrimary = false) => {
    try {
        const response = await api.post("/communities/process-invitation", {
            community_id: communityId,
            token,
            make_primary: makePrimary
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get community change requests (admin only)
 * @param {number} communityId The ID of the community
 * @returns {Promise<Array>} List of community change requests
 */
export const getCommunityRequests = async (communityId) => {
    try {
        const response = await api.get(`/communities/${communityId}/admin/requests`);
        return response.data || [];
    } catch (error) {
        throw error;
    }
};

/**
 * Accept a community change request (admin only)
 * @param {number} communityId The ID of the community
 * @param {number} requestId The ID of the request to accept
 * @returns {Promise<Object>} Response data
 */
export const acceptCommunityRequest = async (communityId, requestId) => {
    try {
        const response = await api.post(`/communities/${communityId}/admin/requests/accept`, { request_id: requestId });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Reject a community change request (admin only)
 * @param {number} communityId The ID of the community
 * @param {number} requestId The ID of the request to reject
 * @param {string} reason The reason for rejection
 * @returns {Promise<Object>} Response data
 */
export const rejectCommunityRequest = async (communityId, requestId, reason = "") => {
    try {
        const response = await api.post(`/communities/${communityId}/admin/requests/reject`, { 
            request_id: requestId,
            reason
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get invitation details without requiring community membership
 * @param {string} token The invitation token
 * @param {number} communityId The ID of the community
 * @returns {Promise<Object>} Community data for the invitation
 */
export const getInvitationDetails = async (token, communityId) => {
    try {
        const response = await api.get('/communities/invitation-details', {
            params: { token, community_id: communityId }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Check if the current user can edit content in the specified community
 * @param {number} communityId The ID of the community to check
 * @param {object} authContext The authentication context (containing userCommunities, isAdminOfCommunity)
 * @returns {boolean} Whether the user has edit permission
 */
export const canEditCommunityContent = (communityId, authContext) => {
    const { isGlobalAdmin, isAdminOfCommunity, userCommunities } = authContext;
    
    // Global admins can edit any community content
    if (isGlobalAdmin) {
      return true;
    }
    
    // Community admins can edit their community content
    if (isAdminOfCommunity && isAdminOfCommunity(communityId)) {
      return true;
    }
    
    // Find the community in user's communities
    const community = userCommunities.find(c => c.id === parseInt(communityId));
    
    // If not a member, can't edit
    if (!community) {
      return false;
    }
    
    // Convert to boolean more reliably
    return Boolean(community.allow_member_edits);
};
  
  /**
   * Normalize community visibility value
   * @param {string} visibility The raw visibility value from the API
   * @returns {string} Normalized visibility: 'Public' or 'Private'
   */
  export const normalizeVisibility = (visibility) => {
    // Treat empty string, null, undefined or 'Public' as 'Public'
    if (!visibility || visibility === '' || visibility === 'Public') {
      return 'Public';
    }
    // Everything else is treated as 'Private'
    return 'Private';
  };
  
  /**
   * Get visibility display properties
   * @param {string} visibility The visibility value to normalize and translate
   * @param {string} language The language code ('en', 'sl', etc.)
   * @returns {object} Object with icon, text, and badge variant
   */
  export const getVisibilityDisplay = (visibility, language = 'sl') => {
    const normalized = normalizeVisibility(visibility);
    
    // Default English translations
    let translations = {
      Public: {
        text: 'Public',
        icon: 'faGlobe',
        variant: 'info'
      },
      Private: {
        text: 'Private',
        icon: 'faLock', 
        variant: 'secondary'
      }
    };
    
    // Slovenian translations
    if (language === 'sl') {
      translations = {
        Public: {
          text: 'Javna',
          icon: 'faGlobe',
          variant: 'info'
        },
        Private: {
          text: 'Zasebna',
          icon: 'faLock',
          variant: 'secondary'
        }
      };
    }
    
    return translations[normalized];
  };
  
  /**
   * Check if a user is a member of the specified community
   * @param {number} communityId The ID of the community
   * @param {array} userCommunities Array of user's communities from auth context
   * @returns {boolean} Whether the user is a member
   */
  export const isMemberOfCommunity = (communityId, userCommunities) => {
    return userCommunities.some(c => c.id === parseInt(communityId));
  };